import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import SaveModelAssignment from '@Application/Ai/AiConfiguration/Assignments/Commands/SaveModelAssignment';
import saveModelAssignment from '@Application/Ai/AiConfiguration/Assignments/saveModelAssignment';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import LiveUsageSwitch from '@UI/Pages/AiAssignments/Shared/UsageAssignmentCard/UsageAssignmentCardActions/LiveUsageSwitch';
import writePermissionByScope from '@UI/Pages/AiAssignments/Shared/UsageAssignmentCard/UsageAssignmentCardActions/Helpers/writePermissionByScope';
import { openUsageSettingsSidebar } from '@UI/Pages/AiAssignments/Shared/Signals/usageSettingsSidebarSignal';
import type AiModelsListView from '@UI/Store/AiConfiguration/Views/AiModelsListView';
import type ModelAssignmentView from '@UI/Store/AiConfiguration/Views/ModelAssignmentView';
import { css } from '@emotion/react';
import { useDispatch } from '@verclam/synapse';
import { Button, Dropdown } from '@verclam/ui-kit';
import { SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { fieldWithoutMarginStyles } from '@Guidelines/Theme/commonPassthrough';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const dropdownStyle = css`
	min-width: 14rem;
	flex: 1;
`;

/**
 * Leaf action: dispatches itself, never a callback prop. Choosing a model from the dropdown always
 * writes an override at `scope` (never the inherited level below). Reverting that override is a
 * separate leaf action, `UsageAssignmentCardRevertAction`, in the card's header row rather than here.
 * A live usage wraps the row in `LiveUsageSwitch`, which shows it only while the live feature is on.
 */
type Props = {
	readonly assignment: ModelAssignmentView;
	readonly scope: AssignmentScope;
	readonly models: AiModelsListView;
};

const UsageAssignmentCardActions: React.FC<Props> = ({ assignment, scope, models }) => {
	const dispatch = useDispatch();

	const availableModels = React.useMemo(
		() => models.list.filter((model) => model.usages.includes(assignment.usage.value)),
		[models, assignment.usage.value],
	);

	const handleModelChange = React.useCallback(
		(modelId: number | null) => {
			if (modelId === null) {
				return;
			}

			dispatch(
				saveModelAssignment(new SaveModelAssignment(scope, assignment.usage.value, modelId)),
			).catch(ignoreHandledError);
		},
		[dispatch, scope, assignment.usage.value],
	);

	const handleOpenSettings = React.useCallback(() => {
		openUsageSettingsSidebar(assignment.usage.value);
	}, [assignment.usage.value]);

	const row = (
		<div css={actionsRowStyle}>
			<div css={dropdownStyle}>
				<Dropdown<number>
					name={`usage-assignment-${assignment.usage.value}`}
					label={`Modèle pour ${assignment.usage.label}`}
					isLabelHidden
					options={availableModels}
					optionLabel="label"
					optionValue="id"
					required={assignment.isLive}
					value={assignment.ownModelId}
					placeholder="Choisir un modèle"
					onChange={handleModelChange}
					pt={fieldWithoutMarginStyles}
				/>
			</div>
			<WithPermission permission={writePermissionByScope[scope.type]}>
				{/* No `aria-label` here: the kit's `Button` always lets a non-empty `label` win over it
				(Button.doc.md, "Le nom accessible"), so the accessible name is shared across every card. */}
				<Button
					type="button"
					severity="secondary"
					outlined
					label="Régler l’appel"
					icon={<SlidersHorizontal size={16} />}
					onClick={handleOpenSettings}
				/>
			</WithPermission>
		</div>
	);

	return assignment.isLive ? (
		<LiveUsageSwitch assignment={assignment} scope={scope}>
			{row}
		</LiveUsageSwitch>
	) : (
		row
	);
};

export default UsageAssignmentCardActions;
